import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CRow,
  CSpinner,
} from '@coreui/react';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { useMantenimientoFlota } from './hooks/useMantenimientoFlota.js';

const ESTADOS = [
  { value: 'ACTIVO', label: 'Activo' },
  { value: 'INACTIVO', label: 'Inactivo' },
];

function toInputDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export default function FormularioMantenimientoFlota() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { sucursal } = useAuth();
  const { loading, error, permissions, getById, create, update } = useMantenimientoFlota();
  const [form, setForm] = useState({
    unidad: '',
    patente: '',
    descripcion: '',
    fechaProgramada: '',
    fechaRealizada: '',
    costoEstimado: '',
    proveedor: '',
    estado: 'ACTIVO',
    sucursalId: sucursal?.id ? String(sucursal.id) : '',
  });

  const canSubmit = useMemo(() => {
    if (isEditing) return permissions.canEdit;
    return permissions.canCreate;
  }, [isEditing, permissions.canCreate, permissions.canEdit]);

  useEffect(() => {
    if (!isEditing) return;
    const loadData = async () => {
      const item = await getById(id);
      if (item) {
        setForm({
          unidad: item.unidad || '',
          patente: item.patente || '',
          descripcion: item.descripcion || '',
          fechaProgramada: toInputDate(item.fechaProgramada),
          fechaRealizada: toInputDate(item.fechaRealizada),
          costoEstimado: item.costoEstimado != null ? String(item.costoEstimado) : '',
          proveedor: item.proveedor || '',
          estado: item.estado || 'ACTIVO',
          sucursalId: item.sucursalId ? String(item.sucursalId) : '',
        });
      }
    };
    loadData();
  }, [getById, id, isEditing]);

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const payload = {
      unidad: form.unidad.trim(),
      patente: form.patente.trim() || null,
      descripcion: form.descripcion.trim() || null,
      fechaProgramada: form.fechaProgramada ? new Date(form.fechaProgramada).toISOString() : null,
      fechaRealizada: form.fechaRealizada ? new Date(form.fechaRealizada).toISOString() : null,
      costoEstimado: form.costoEstimado ? Number(form.costoEstimado) : null,
      proveedor: form.proveedor.trim() || null,
      estado: form.estado,
      sucursalId: form.sucursalId || null,
    };

    const result = isEditing ? await update(id, payload) : await create(payload);
    if (result) {
      navigate('/mantenimiento-flota');
    }
  };

  if (!permissions.canView) {
    return (
      <CAlert color="danger" className="m-4">
        No tenés permisos para acceder al módulo de Mantenimiento de Flota.
      </CAlert>
    );
  }

  if (!canSubmit) {
    return (
      <CAlert color="danger" className="m-4">
        No tenés permisos para {isEditing ? 'editar' : 'crear'} mantenimientos.
      </CAlert>
    );
  }

  return (
    <div className="p-4">
      <CCard>
        <CCardHeader>
          <h4 className="mb-1">{isEditing ? 'Editar mantenimiento' : 'Nuevo mantenimiento'}</h4>
          <p className="mb-0 text-medium-emphasis">
            Registrá tareas, fechas y responsables por unidad de flota.
          </p>
        </CCardHeader>
        <CCardBody>
          {error && <CAlert color="danger">{error}</CAlert>}
          <CForm onSubmit={handleSubmit}>
            <CRow className="g-3">
              <CCol xs={12} md={6}>
                <CFormInput
                  label="Unidad / Vehículo"
                  name="unidad"
                  value={form.unidad}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Camión 12"
                />
              </CCol>
              <CCol xs={12} md={3}>
                <CFormInput
                  label="Patente"
                  name="patente"
                  value={form.patente}
                  onChange={handleChange}
                  placeholder="AA123BB"
                />
              </CCol>
              <CCol xs={12} md={3}>
                <CFormInput
                  label="Sucursal"
                  name="sucursalId"
                  value={form.sucursalId}
                  onChange={handleChange}
                  placeholder="ID sucursal"
                  required
                />
              </CCol>
              <CCol xs={12} md={6}>
                <CFormInput
                  type="date"
                  label="Fecha programada"
                  name="fechaProgramada"
                  value={form.fechaProgramada}
                  onChange={handleChange}
                />
              </CCol>
              <CCol xs={12} md={6}>
                <CFormInput
                  type="date"
                  label="Fecha realizada"
                  name="fechaRealizada"
                  value={form.fechaRealizada}
                  onChange={handleChange}
                />
              </CCol>
              <CCol xs={12} md={4}>
                <CFormInput
                  type="number"
                  label="Costo estimado"
                  name="costoEstimado"
                  value={form.costoEstimado}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                />
              </CCol>
              <CCol xs={12} md={4}>
                <CFormInput
                  label="Proveedor"
                  name="proveedor"
                  value={form.proveedor}
                  onChange={handleChange}
                  placeholder="Taller o proveedor"
                />
              </CCol>
              <CCol xs={12} md={4}>
                <CFormSelect
                  label="Estado"
                  name="estado"
                  value={form.estado}
                  options={ESTADOS}
                  onChange={handleChange}
                />
              </CCol>
              <CCol xs={12}>
                <CFormTextarea
                  label="Descripción"
                  name="descripcion"
                  value={form.descripcion}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Detalle del mantenimiento"
                />
              </CCol>
            </CRow>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <CButton color="secondary" type="button" onClick={() => navigate('/mantenimiento-flota')}>
                Cancelar
              </CButton>
              <CButton color="primary" type="submit" disabled={loading}>
                {loading && <CSpinner size="sm" className="me-2" />}
                Guardar
              </CButton>
            </div>
          </CForm>
        </CCardBody>
      </CCard>
    </div>
  );
}
